import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
} from "@sinch/cache-helpers";
import type { DeepPartial } from "simplytyped";
import { getMessageMediaApiUrlMiddleware } from "../../config/config";
import type { ApiStrategy, ShellUser } from "../types";
import { MmUserZod, type MmUser } from "./mm.types";

export const MMFetchUserURL = `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/userinfo`;

export const getMmUser: ApiStrategy["getUser"] = async (
  token: string,
  requestInit
) => {
  const response = await window.fetch(MMFetchUserURL, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      [CACHE_HEADER_ENABLED]: "true",
      [CACHE_HEADER_STALE_TIME]: "10000",
      ...requestInit?.headers,
    },
    ...requestInit,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Error fetching user info:", errorText);
    throw new Error(`Failed to fetch user info: ${errorText}`);
  }
  const data = await response.json();
  return toShellUser(data);
};

export const toShellUser = (data: unknown): ShellUser => {
  const parsed = MmUserZod.safeParse(data);
  if (!parsed.success) {
    console.error("Invalid response from userinfo", parsed.error);
  }

  const mmUser = parsed.success ? parsed.data : (data as DeepPartial<MmUser>);

  return {
    id: mmUser.userId ?? "",
    name: `${mmUser.user?.firstName || ""} ${
      mmUser.user?.lastName || ""
    }`.trim(),
    email: mmUser.user?.email ?? "",
    rights: [
      ...(mmUser.account?.features || []),
      ...(mmUser.featureSet?.features || []),
    ],
    language: undefined, // Messagemedia doesn't handle user language as of now.
    profile_image: undefined, // Couldn't find one
    roles: mmUser.roles,
    status: undefined, // Couldn't find one
    activeProject: undefined, // Couldn't find one
    account: {
      id: mmUser.accountId ?? "",
      name: mmUser.account?.company,
      country: mmUser.account?.country,
    },
    settings: {
      businessId: mmUser.account?.businessId,
      vendorId: mmUser.account?.vendorId,
      billingType: mmUser.account?.billingType,
      paymentType: mmUser.account?.paymentType,
      username: mmUser.user?.username,
      timezone: mmUser.account?.timezone,
      defaultCountryCode: mmUser.defaultCountryCode,
      verificationStatus: mmUser.account?.verificationStatus,
      accountCreatedAt: mmUser.account?.createdAt,
    },
    createdAt: mmUser.user?.createdAt,
  };
};
